import React from 'react'
import { format } from 'date-fns'
import { EVICTION_STATUSES } from '@homevest/utils/evictions'
import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  NumberInput,
  NumberInputField,
  Stack,
  Textarea,
  Text
} from '@chakra-ui/react'

import { formatMoney } from 'lib/numbers'
import {
  DateFormField,
  DateTimeFormField,
  DropdownFormField
} from 'components/ChakraToolkit/FormField'
import { Eviction } from './Grid'
import { CANCELATION_OPTIONS, EMPTY_ERRORS, EvictionFormData, STATUS_OPTIONS } from './types'

interface EvictionSidepeekDetailsTabProps {
  eviction: Eviction | null
  formData: EvictionFormData
  canManageEvictions: boolean
  isSaving: boolean
  filingFeeAmount: string
  filingFeeNote: string
  isAddingFilingFee: boolean
  onFormDataChange: (nextFormData: EvictionFormData) => void
  onConsentJudgementChange: (value: boolean) => void
  onFilingFeeAmountChange: (value: string) => void
  onFilingFeeNoteChange: (value: string) => void
  onAddFilingFee: () => void
  onSave: () => void
  onClose: () => void
}

const EvictionSidepeekDetailsTab: React.FC<EvictionSidepeekDetailsTabProps> = ({
  eviction,
  formData,
  canManageEvictions,
  isSaving,
  filingFeeAmount,
  filingFeeNote,
  isAddingFilingFee,
  onFormDataChange,
  onConsentJudgementChange,
  onFilingFeeAmountChange,
  onFilingFeeNoteChange,
  onAddFilingFee,
  onSave,
  onClose
}) => {
  const filingFees = eviction?.eviction_filing_fees ?? []
  const filingFeeTotal = filingFees.reduce(
    (total, filingFee) => total + Number(filingFee.rental_liability.amount),
    0
  )
  const canAddFilingFee = canManageEvictions && !!eviction
  const isFilingFeeAmountInvalid = !filingFeeAmount || Number(filingFeeAmount) <= 0
  const shouldShowCanceledFields = formData.status === EVICTION_STATUSES.CANCELED
  const shouldShowCompletedAt = formData.status === EVICTION_STATUSES.COMPLETED

  return (
    <Stack spacing={5}>
      <DropdownFormField
        field='status'
        label='Status'
        isRequired={false}
        isDisabled={!canManageEvictions}
        errors={EMPTY_ERRORS}
        data={formData}
        setData={onFormDataChange}
        options={STATUS_OPTIONS}
        optionsPlaceholder='Select a status...'
      />

      <DateTimeFormField
        field='filed_at'
        label='Filed At'
        isRequired={formData.status === EVICTION_STATUSES.FILED}
        isDisabled={!canManageEvictions}
        errors={EMPTY_ERRORS}
        data={formData}
        setData={onFormDataChange}
      />

      <DateFormField
        field='court_date'
        label='Court Date'
        isRequired={false}
        isDisabled={!canManageEvictions}
        errors={EMPTY_ERRORS}
        data={formData}
        setData={onFormDataChange}
      />

      <DateFormField
        field='writ_date'
        label='Writ Date'
        isRequired={false}
        isDisabled={!canManageEvictions}
        errors={EMPTY_ERRORS}
        data={formData}
        setData={onFormDataChange}
      />

      <DateFormField
        field='set_out_date'
        label='Set Out Date'
        isRequired={false}
        isDisabled={!canManageEvictions}
        errors={EMPTY_ERRORS}
        data={formData}
        setData={onFormDataChange}
      />

      <DateFormField
        field='projected_possession_date'
        label='Projected Possession Date'
        isRequired={false}
        isDisabled={!canManageEvictions}
        errors={EMPTY_ERRORS}
        data={formData}
        setData={onFormDataChange}
      />

      <DateTimeFormField
        field='file_sent_to_attorney_at'
        label='File Sent to Attorney At'
        isRequired={false}
        isDisabled={!canManageEvictions}
        errors={EMPTY_ERRORS}
        data={formData}
        setData={onFormDataChange}
      />

      {shouldShowCompletedAt && (
        <DateTimeFormField
          field='completed_at'
          label='Completed At'
          isRequired={formData.status === EVICTION_STATUSES.COMPLETED}
          isDisabled={!canManageEvictions}
          errors={EMPTY_ERRORS}
          data={formData}
          setData={onFormDataChange}
        />
      )}

      {shouldShowCanceledFields && (
        <>
          <DateTimeFormField
            field='canceled_at'
            label='Canceled At'
            isRequired={formData.status === EVICTION_STATUSES.CANCELED}
            isDisabled={!canManageEvictions}
            errors={EMPTY_ERRORS}
            data={formData}
            setData={onFormDataChange}
          />

          <DropdownFormField
            field='cancelation_reason'
            label='Cancelation Reason'
            isRequired={formData.status === EVICTION_STATUSES.CANCELED}
            isDisabled={!canManageEvictions}
            errors={EMPTY_ERRORS}
            data={formData}
            setData={onFormDataChange}
            options={CANCELATION_OPTIONS}
            optionsPlaceholder='Select a reason...'
          />
        </>
      )}

      <FormControl isDisabled={!canManageEvictions}>
        <Checkbox
          isChecked={formData.consent_judgement}
          onChange={(e) => onConsentJudgementChange(e.target.checked)}
        >
          Consent Judgement
        </Checkbox>
      </FormControl>

      <Divider />

      <Stack spacing={3}>
        <HStack justify='space-between' align='baseline'>
          <Text fontSize='md' fontWeight='semibold'>
            Filing Fees
          </Text>
          <Text fontSize='sm' fontWeight='medium' color='gray.600'>
            {formatMoney(filingFeeTotal, 2, '$')} total
          </Text>
        </HStack>

        {filingFees.length > 0 ? (
          <Stack spacing={3}>
            {filingFees.map((filingFee) => (
              <Stack key={filingFee.id} spacing={1}>
                <Text fontSize='sm' fontWeight='medium'>
                  {formatMoney(Number(filingFee.rental_liability.amount), 2, '$')}
                </Text>
                {filingFee.rental_liability.note && (
                  <Text color='gray.600' fontSize='xs' whiteSpace='pre-wrap'>
                    {filingFee.rental_liability.note}
                  </Text>
                )}
                {filingFee.charged_by_admin && (
                  <Text color='gray.500' fontSize='xs'>
                    Added by {filingFee.charged_by_admin.first_name}{' '}
                    {filingFee.charged_by_admin.last_name}
                  </Text>
                )}
                <Text color='gray.500' fontSize='xs'>
                  Added on {format(new Date(filingFee.charged_at), 'MM/dd/yyyy h:mm a')}
                </Text>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Text color='gray.500' fontSize='sm'>
            No filing fees added yet.
          </Text>
        )}

        {canAddFilingFee ? (
          <>
            <FormControl isDisabled={!canAddFilingFee || isAddingFilingFee}>
              <FormLabel mb={1}>Amount</FormLabel>
              <NumberInput
                value={filingFeeAmount}
                onChange={(valueAsString) => onFilingFeeAmountChange(valueAsString)}
                precision={2}
                min={0}
                step={0.01}
              >
                <NumberInputField placeholder='350.00' />
              </NumberInput>
            </FormControl>

            <FormControl isDisabled={!canAddFilingFee || isAddingFilingFee}>
              <FormLabel mb={1}>Note (optional)</FormLabel>
              <Textarea
                value={filingFeeNote}
                onChange={(e) => onFilingFeeNoteChange(e.target.value)}
                placeholder='Eviction filing fee'
                maxLength={500}
              />
            </FormControl>

            <HStack justify='flex-end'>
              <Button
                colorScheme='teal'
                variant='outline'
                size='xs'
                onClick={onAddFilingFee}
                isDisabled={!canAddFilingFee || isAddingFilingFee || isFilingFeeAmountInvalid}
                isLoading={isAddingFilingFee}
              >
                Add Filing Fee
              </Button>
            </HStack>
          </>
        ) : null}
      </Stack>

      <HStack justify='flex-end'>
        <Button variant='ghost' size='xs' onClick={onClose}>
          Cancel
        </Button>
        <Button
          colorScheme='teal'
          size='xs'
          onClick={onSave}
          isDisabled={!canManageEvictions || isSaving}
          isLoading={isSaving}
        >
          Save
        </Button>
      </HStack>
    </Stack>
  )
}

export default EvictionSidepeekDetailsTab
